import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProviders } from './useProviders'
import {
	VIDEO_MODEL_ID,
	DEFAULT_VIDEO_PROMPT,
	PERSISTED_UPLOADS_KEY,
} from '../pages/generate/utils/constants'
import type { Model } from '../data/models'
import type { GenerationCard } from '../types/generationCard'
import { resetScene } from '../api/sceneSelection'

export { DEFAULT_VIDEO_PROMPT }

// ── Helpers ──────────────────────────────────────────────────

/**
 * Finds the video model used for image-to-video generation.
 * Prefers kling-2.6-i2v, falls back to any video model that requires an image.
 */
export function findVideoModel(models: Model[]): Model | null {
	return (
		models.find((m) => m.id === VIDEO_MODEL_ID) ??
		models.find((m) => m.category === 'video' && m.requiresImage) ??
		null
	)
}

/**
 * Writes a create-video request to localStorage so that /bots/video
 * can pick it up via BotsVideo → handleCreateVideoRequest.
 * BotsVideo reads imageUrl, calls handleCreateVideoRequest which
 * applies the card directly to the widget (open or pending).
 */
export function writeCreateVideoRequest(imageUrl: string, modelId: string = VIDEO_MODEL_ID): void {
	localStorage.setItem(
		PERSISTED_UPLOADS_KEY,
		JSON.stringify({
			uploadedImageUrl: imageUrl,
			swapImageUrl: null,
			modelId,
			prompt: DEFAULT_VIDEO_PROMPT,
			savedAt: Date.now(),
		}),
	)
}

// ── Hook ─────────────────────────────────────────────────────

/**
 * useCreateVideo — универсальный хук для запуска создания видео.
 *
 * createVideoNavigate(imageUrl):
 *   Пишет данные в localStorage и переходит на /bots/video.
 *   Используется из Files.tsx, BotsImages.tsx, GenerateWidget.tsx.
 *
 * findVideoModel(models):
 *   Экспортированный хелпер — используется в Generate.tsx (inline-режим).
 */
export function useCreateVideo() {
	const navigate = useNavigate()
	const { allModels } = useProviders()

	const createVideoNavigate = useCallback(
		(imageUrl: string) => {
			const videoModel = findVideoModel(allModels)
			if (!videoModel) return
			// «Создать видео ИЗ ЭТОГО кадра» — намерение конкретное: оживить
			// готовую картинку. Персонаж тут лишний и вреден: липкий выбор
			// переприменялся на новой странице и вставал в слот ВМЕСТО
			// результата, а i2v-модель (Kling) на него ругалась «персонаж
			// стал бы просто первым кадром» (жалоба владельца 2026-07-25).
			// Сам персонаж в кадре уже есть — он в нём и сгенерирован.
			resetScene()
			writeCreateVideoRequest(imageUrl, videoModel.id)
			navigate('/bots/video')
		},
		[allModels, navigate],
	)

	return { createVideoNavigate }
}

/**
 * Builds a GenerationCard configured for video generation.
 * Used in Generate.tsx inline mode.
 */
export function buildVideoCard(card: GenerationCard, videoModelId: string): GenerationCard {
	return {
		...card,
		prompt: DEFAULT_VIDEO_PROMPT,
		// Оригинал файла, не previewUrl: у карточек бэк-ленты (студия) превью —
		// лёгкий WEBP, и видео-провайдеры отвергают его формат («формат файла
		// не поддерживается» у Kling), а разрешение превью занижено. У локальной
		// истории fullUrl пуст — previewUrl там и есть оригинал.
		sourceImageUrl: card.fullUrl || card.previewUrl,
		sourceImageDbKey: undefined,
		modelId: videoModelId,
		settings: undefined,
	}
}
